import { useState, useCallback } from "react";
import JSZip from "jszip";
import { parseXMLFiles } from "../parsers/xmlParser";
import { SOURCE_TYPE } from "../utils/dataSource";
import { normalizeId } from "../utils/formatters";

// טוען קובץ/קבצי ZIP אחד או יותר ומנתב לפי SUG-MIMSHAK:
//   1 = ממשק אחזקות  → addHoldingsFunds (איחוד per-ת"ז, ריבוי לקוחות בקובץ)
//   2/אחר = טרום ייעוץ → addClient (לקוח בודד, מודל datasets)
export function useBatchLoader({ addClient, addHoldingsFunds }) {
  const [status, setStatus] = useState(null); // { type, msg, errors? }
  const [busy, setBusy] = useState(false);
  const [lastAdviceId, setLastAdviceId] = useState(null);
  const [lastClientId, setLastClientId] = useState(null);
  const [lastSource, setLastSource] = useState(null);

  const loadZips = useCallback(
    async (files) => {
      const list = Array.from(files || []).filter((f) => {
        const lower = f.name.toLowerCase();
        return lower.endsWith(".zip") || lower.endsWith(".dat") || lower.endsWith(".xml") || lower.endsWith(".arj");
      });
      if (!list.length) {
        setStatus({ type: "error", msg: "לא נבחרו קבצי מערכת תקינים (ZIP, DAT, XML, ARJ)." });
        return;
      }

      setBusy(true);
      let adviceCount = 0;
      let holdingsClients = 0;
      let holdingsFunds = 0;
      let adviceLastId = null;
      let loadedClientId = null;
      let loadedSource = null;
      const errors = [];

      let allXmlContents = [];
      const perFile = []; // { name, contents } — לטעינת אחזקות לפי קובץ מקור
      let totalFileItemCount = 0;

      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        setStatus({ type: "loading", msg: `קורא ${i + 1}/${list.length}: ${file.name}…` });
        try {
          if (file.name.toLowerCase().endsWith(".zip")) {
            const zip = await JSZip.loadAsync(file);
            const xmlEntries = Object.values(zip.files).filter(
              (f) =>
                !f.dir &&
                (f.name.toLowerCase().endsWith(".xml") || f.name.toLowerCase().endsWith(".dat"))
            );
            if (!xmlEntries.length) {
              errors.push(`${file.name}: לא נמצאו קבצי XML/DAT בארכיון`);
              continue;
            }
            const contents = await Promise.all(
              xmlEntries.map(async (entry) => {
                const buf = await entry.async("arraybuffer");
                const sniff = new TextDecoder("utf-8").decode(new Uint8Array(buf, 0, 200));
                const encMatch = sniff.match(/encoding=["']([^"']+)["']/i);
                const enc = encMatch ? encMatch[1] : "utf-8";
                return { name: entry.name, content: new TextDecoder(enc).decode(buf) };
              })
            );
            allXmlContents.push(...contents);
            perFile.push({ name: file.name, contents });
            totalFileItemCount += xmlEntries.length;
          } else {
            // Raw XML or DAT file
            const buf = await file.arrayBuffer();
            const sniff = new TextDecoder("utf-8").decode(new Uint8Array(buf, 0, 200));
            const encMatch = sniff.match(/encoding=["']([^"']+)["']/i);
            const enc = encMatch ? encMatch[1] : "utf-8";
            const item = { name: file.name, content: new TextDecoder(enc).decode(buf) };
            allXmlContents.push(item);
            perFile.push({ name: file.name, contents: [item] });
            totalFileItemCount += 1;
          }
        } catch (err) {
          errors.push(`${file.name}: ${err.message}`);
        }
      }

      if (allXmlContents.length > 0) {
        setStatus({ type: "loading", msg: `מפענח נתונים…` });
        try {
          const { funds, taarichBitzua, sugMimshak } = parseXMLFiles(allXmlContents);
          if (!funds.length) {
            errors.push(`לא נמצאו מוצרים בקבצים שנטענו`);
          } else {
            const toIso = (d) => {
              const m = String(d || "").match(/(\d{2})\/(\d{2})\/(\d{4})/);
              return m ? `${m[3]}-${m[2]}-${m[1]}` : null;
            };
            const latestOf = (getter) => {
              let bestIso = null, val = null;
              for (const f of funds) {
                const c = getter(f);
                const iso = toIso(c);
                if (iso && (!bestIso || iso > bestIso)) { bestIso = iso; val = c; }
              }
              return val;
            };
            const actualDate =
              latestOf((f) => f.validDate) ||
              latestOf((f) => f.yitrot?.date) ||
              taarichBitzua;

            if (sugMimshak === "1") {
              // ממשק אחזקות — ריבוי לקוחות. נטען לפי קובץ מקור, כדי שכל קרן תקבל
              // את שם הקובץ האמיתי (ולא "קבצים מרובים") — אחרת כלל עדיפות ה-ZIP
              // ב-addHoldingsFunds מדלג על עדכון קרנות קיימות, והמקורות לא נרשמים.
              for (const pf of perFile) {
                const parsed = parseXMLFiles(pf.contents);
                const pfFunds = parsed.funds;
                if (!pfFunds.length) continue;
                const res = addHoldingsFunds(
                  pfFunds,
                  {
                    fileName: pf.name,
                    taarichBitzua: actualDate,
                    shemYatzran: pfFunds[0]?.companyName || null,
                    sourceType: SOURCE_TYPE.MASLEKA,
                  },
                  { replaceExisting: true }
                );
                holdingsClients += res.clientsTouched;
                holdingsFunds += res.fundsAdded;
                loadedSource = "holdings";
                const touchedIds = res.clientIds || [...new Set(pfFunds.map(f => normalizeId(f.client?.id)).filter(id => id && id !== "—"))];
                if (touchedIds.length > 0) {
                  loadedClientId = touchedIds[0];
                }
              }
            } else {
              // טרום ייעוץ — יכול להכיל ריבוי לקוחות
              const byClient = new Map();
              for (const f of funds) {
                const cid = f.client?.id || "—";
                if (!byClient.has(cid)) byClient.set(cid, []);
                byClient.get(cid).push(f);
              }

              for (const [clientId, clientFunds] of byClient.entries()) {
                const normCid = normalizeId(clientId);
                const fileInfo = {
                  name: list.length === 1 ? list[0].name : "קבצים מרובים",
                  xmlCount: totalFileItemCount,
                  fundsCount: clientFunds.length,
                  client: clientFunds[0]?.client?.name || "—",
                  clientId: normCid,
                  taarichBitzua: actualDate,
                  sugMimshak,
                  sourceType: SOURCE_TYPE.MASLEKA,
                };
                const id = addClient(clientFunds, fileInfo);
                if (id) adviceLastId = id;
                loadedClientId = normCid;
                loadedSource = "advice";
                adviceCount++;
              }
            }
          }
        } catch (err) {
          errors.push(`שגיאה בפענוח: ${err.message}`);
        }
      }

      setBusy(false);
      if (adviceLastId) setLastAdviceId(adviceLastId);
      if (loadedClientId) setLastClientId(loadedClientId);
      if (loadedSource) setLastSource(loadedSource);

      const parts = [];
      if (adviceCount) parts.push(`${adviceCount} קבצי טרום-ייעוץ`);
      if (holdingsFunds || holdingsClients) parts.push(`${holdingsFunds} קרנות אחזקות (${holdingsClients} לקוחות)`);
      if (errors.length) parts.push(`${errors.length} שגיאות`);
      const noData = !adviceCount && !holdingsFunds && !holdingsClients;
      const errorMsg = errors.length ? ` (${errors.join(", ")})` : "";
      setStatus({
        type: noData ? "error" : errors.length ? "warning" : "success",
        msg: noData ? `לא נטענו נתונים${errorMsg}` : `נטענו: ${parts.join(" · ")}${errorMsg}`,
        errors,
      });
    },
    [addClient, addHoldingsFunds]
  );

  const reset = useCallback(() => {
    setStatus(null);
    setLastAdviceId(null);
    setLastClientId(null);
    setLastSource(null);
  }, []);

  return { loadZips, status, busy, lastAdviceId, lastClientId, lastSource, reset };
}
